// Copyright (c) Docugami, Inc. All rights reserved.

import { SlidingPanel } from "components/cards/SlidingPanel";
import { DocSetStatus } from "components/cluster_management/DocSetStatus";
import { useAppSelector } from "hooks/redux";
import { useEffect, useMemo, useState } from "react";
import { selectDocSetMinFiles } from "store/organization/organizationSlice";
import styled from "styled-components";
import { getAutomationIdForDocsetProgressIndicator } from "utils/Automation";
import { BubbleProgressBar, IndicatorDetails, IndicatorTooltipProps, IndicatorTooltipVisibility } from "./BubbleProgressBar";

const Root = styled.div`
    display: flex;
    width: 100%;
    box-sizing: border-box;
    padding: 20px 100px 50px;
    min-width: 0;
    justify-content: center;

    ${props => !!props.hidden && 'display: none'};
`;

export enum DocSetIndicatorSteps {
    DocSetCreated = "DocSetCreated",
    MinimumDocumentsAdded = "MinimumDocumentsAdded",
    DocSetReady = "DocSetReady"
}

interface DocSetStatusIndicatorProps {
    docSetId: string;
    hidden?: boolean;
    status?: DocSetStatus;
}

export const DocSetStatusIndicator = (props: DocSetStatusIndicatorProps): JSX.Element => {
    const [isCollapsed, setIsCollapsed] = useState(true);

    const docSetDocThreshold = useAppSelector(selectDocSetMinFiles);

    useEffect(() => {
        setIsCollapsed(true);
    }, [props.docSetId])

    useEffect(() => {
        let timeoutId: NodeJS.Timeout | undefined = undefined;
        if (props.status === DocSetStatus.Ready) {
            if (!isCollapsed) {
                // The indicator was previously open, let it stay open for 5s then collapse it
                timeoutId = setTimeout(() => setIsCollapsed(true), 1000);
            }
        } else if (!!props.status) {
            setIsCollapsed(false);
        }

        return () => { if (timeoutId !== undefined) clearTimeout(timeoutId) }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.status])

    const indicatorSteps = [DocSetIndicatorSteps.DocSetCreated, DocSetIndicatorSteps.MinimumDocumentsAdded, DocSetIndicatorSteps.DocSetReady];
    const completedStep = useMemo(() => {
        if (props.status === DocSetStatus.Ready) {
            return DocSetIndicatorSteps.DocSetReady;
        }

        if (props.status !== DocSetStatus.Incomplete) {
            return DocSetIndicatorSteps.MinimumDocumentsAdded;
        }

        return DocSetIndicatorSteps.DocSetCreated;
    }, [props.status]);

    function getTooltipPropsForIndicatorStep(step: DocSetIndicatorSteps): IndicatorTooltipProps | undefined {
        switch (step) {
            case DocSetIndicatorSteps.MinimumDocumentsAdded:
                return {
                    tooltip: `This doc set does not have the minimum number of documents required to be complete.${docSetDocThreshold !== undefined ? ` Docugami requires ${docSetDocThreshold} or more documents of a similar type to process your doc set.` : ''}`,
                    visibility: IndicatorTooltipVisibility.Incomplete
                }
            case DocSetIndicatorSteps.DocSetReady:
            case DocSetIndicatorSteps.DocSetCreated:
            default:
                break;
        }
    }
    
    function getIndicatorDetailsForStep(step: DocSetIndicatorSteps): IndicatorDetails {
        return {
            label: getLabelForIndicatorStep(step),
            tooltipProps: getTooltipPropsForIndicatorStep(step),
            automationId: getAutomationIdForDocsetProgressIndicator(step)
        }
    }

    return (
        <SlidingPanel isOpen={!isCollapsed}>
            <Root hidden={props.hidden}>
                <BubbleProgressBar
                    indicators={indicatorSteps.map(getIndicatorDetailsForStep)}
                    completedUntilIndex={indicatorSteps.findIndex(x => x === completedStep)} />
            </Root>
        </SlidingPanel>
    )
}

function getLabelForIndicatorStep(step: DocSetIndicatorSteps): string {
    switch (step) {
        case DocSetIndicatorSteps.DocSetCreated:
            return "Doc Set Created"
        case DocSetIndicatorSteps.MinimumDocumentsAdded:
            return "Minimum Documents Added"
        case DocSetIndicatorSteps.DocSetReady:
            return "Ready For Use!"
        default:
            return ""
    }
}